import { MintedSession, MintResult } from "./dictation-session";

/**
 * One cached pre-minted dictation session (§ A1 of
 * docs/superpowers/specs/2026-08-06-dictation-ux-latency-design.md). Framework-free
 * so it is testable without Angular; DictationService owns the singleton instance.
 *
 * CONSUME-ON-USE is the load-bearing rule: a client secret replayed into two
 * WebRTC calls collapses both sessions onto one ticket (usage attribution)
 * and may simply fail the second connect. take() therefore hands a mint out
 * at most once.
 *
 * No refresh loop: prewarm() re-mints only when a caller triggers it, so an
 * idle open dialog never re-mints.
 */
export class MintCache {
    private cached: MintedSession | null = null;
    private inFlight = false;

    /** A cached mint must have at least this much validity left to be usable. */
    private static readonly FRESHNESS_MARGIN_MS = 5000;

    prewarm(mint: () => Promise<MintResult>, nowMs: () => number = () => Date.now()): void {
        if (this.inFlight) return;
        if (this.isFresh(nowMs())) return; // don't spend a mint replacing a good one
        this.inFlight = true;
        void (async () => {
            try {
                const result = await mint();
                if (result.ok) this.cached = result.minted;
            } catch {
                /* prewarm is best-effort; the click falls back to a fresh mint */
            }
            this.inFlight = false;
        })();
    }

    /** Consume-on-use: a cached mint is handed out at most once, and only while fresh. */
    take(nowMs: number = Date.now()): MintedSession | null {
        const hit = this.isFresh(nowMs) ? this.cached : null;
        this.cached = null;
        return hit;
    }

    private isFresh(nowMs: number): boolean {
        return !!this.cached && this.cached.expiresAt * 1000 - nowMs > MintCache.FRESHNESS_MARGIN_MS;
    }
}
